import { acceptHMRUpdate, defineStore } from "pinia";
import type FamilyGroup from "../../../backend/app/models/family_group";

type User = {
  id: number;
  firstname: string;
  surname: string;
  email?: string;
  role?: string;
  avatar?: string;
};

export const useGroupStore = defineStore("familyGroup", () => {
  const { $setItem, $getItem } = useNuxtApp();
  const transmitStore = useTransmitStore();
  const groups: Ref<FamilyGroup[] | null> = ref(null);
  const sortedGroups: Ref<Record<number, FamilyGroup> | null> = ref(null);
  const currentSelectedGroup: Ref<FamilyGroup | null> = useState(
    "familyGroup.currentSelectedGroup",
    () => null,
  );
  // Users cache for the current selected group
  const currentGroupUsers: Ref<User[]> = useState("familyGroup.currentGroupUsers", () => []);
  const usersLoading: Ref<boolean> = ref(false);
  const usersError: Ref<string | null> = ref(null);

  // Weekly Digest State
  const weeklyDigest = ref<any | null>(null);
  const digestLoading = ref(false);
  const weeklyDigests = ref<{ data: any[]; meta: any }>({
    data: [],
    meta: { total: 0, lastPage: 1, currentPage: 1 },
  });
  const digestsLoading = ref(false);
  const digestPage = ref(1);
  const digestTotalPages = ref(1);

  const filterByIds = (ids) => groups.value.filter((item) => ids.includes(item.id));

  // Sync groups and group users requests
  watch(
    [currentSelectedGroup, groups],
    ([selectedGroup, _groups]) => {
      if (import.meta.client) {
        if (selectedGroup && !usersLoading.value) {
          fetchGroupUsers(selectedGroup.id);
        }
      }
    },
    { immediate: true },
  );

  // Get user's available groups (computed)
  const userGroups = computed(() => {
    const auth = useAuthStore();
    if (!auth?.user || !groups.value) return [];

    // Users with view_all (owners, hr_managers, auditors) see every group in the org.
    if (auth.permissions?.familyGroups?.includes("view_all")) {
      return groups.value;
    }

    // Everyone else is limited to their assigned groups
    if (!auth.user.family_groups) return [];

    const userGroupIds = auth.user.family_groups.map((group) => group.id);
    return groups.value.filter((group) => userGroupIds.includes(group.id));
  });

  function sortGroupsByReference(
    targetArray: FamilyGroup[],
    referenceArray: number[],
  ): FamilyGroup[] {
    const referenceIndexMap = new Map(referenceArray.map((id, index) => [id, index]));

    return targetArray
      .slice() // Create a copy to avoid mutating the original array
      .sort((a, b) => {
        const indexA = referenceIndexMap.has(a.id) ? referenceIndexMap.get(a.id)! : Infinity;
        const indexB = referenceIndexMap.has(b.id) ? referenceIndexMap.get(b.id)! : Infinity;

        return indexA - indexB;
      });
  }

  function getGroupIcon(familyGroupId: number) {
    return groups.value.find((group) => group.id === familyGroupId)?.icon;
  }

  function getGroupInfo(familyGroupId: number) {
    return groups.value.find((group) => group.id === familyGroupId);
  }

  // Fetch users for a specific family group
  async function fetchGroupUsers(groupId: number) {
    try {
      // Check if we're in a proper Nuxt context
      try {
        const nuxtApp = useNuxtApp();
        if (!nuxtApp) {
          return;
        }
      } catch (error) {
        console.log("Nuxt app not ready, skipping fetchGroupUsers", error);
        return;
      }

      usersLoading.value = true;
      usersError.value = null;

      const auth = useAuthStore();

      // Check if auth store is properly initialized
      if (!auth || typeof auth.api !== "function") {
        usersLoading.value = false;
        return;
      }

      const response = (await auth.api("post", `/orga/fg/users/`, {
        groupId,
      })) as { users: User[] | null };
      if (response) {
        currentGroupUsers.value = response.users as User[];
      } else {
        currentGroupUsers.value = [];
      }
    } catch (err: unknown) {
      console.error("Error fetching family group users:", err);
      usersError.value = (err as Error).message || "Failed to load users";
      currentGroupUsers.value = [];
    } finally {
      usersLoading.value = false;
    }
  }

  async function fetchWeeklyDigest(groupId: number) {
    if (!groupId) return;
    digestLoading.value = true;
    try {
      const auth = useAuthStore();
      const response = await auth.api("GET", `/digest/${groupId}`);
      weeklyDigest.value = response;
    } catch (error) {
      console.error("Error fetching weekly digest:", error);
      weeklyDigest.value = null;
    } finally {
      digestLoading.value = false;
    }
  }

  async function fetchWeeklyDigests(groupId: number, page: number = 1, limit: number = 10) {
    if (!groupId) return;
    digestsLoading.value = true;
    try {
      const auth = useAuthStore();
      const response = await auth.api("GET", `/digests/${groupId}`, {
        page,
        limit,
      });
      weeklyDigests.value = response;
      digestPage.value = response.meta.currentPage || 1;
      digestTotalPages.value = (response.meta.lastPage ?? 0) + 1;
    } catch (error) {
      console.error("Error fetching weekly digests:", error);
      weeklyDigests.value = { data: [], meta: { total: 0, lastPage: 1, currentPage: 1 } };
    } finally {
      digestsLoading.value = false;
    }
  }

  async function setCurrentSelectedGroup(group: FamilyGroup | null) {
    // Clean up previous family group subscription if there was one
    if (currentSelectedGroup.value?.id) {
      await transmitStore.unsubscribeFamilyGroup(currentSelectedGroup.value.id);
    }

    currentSelectedGroup.value = group;

    // Create new family group subscription if group is selected
    if (group?.id) {
      await transmitStore.subscribeFamilyGroup(group.id);
    }

    if (group) {
      $setItem("current_selected_group", group.id.toString());
    } else {
      $setItem("current_selected_group", "");
      // Clear users when no group is selected
      currentGroupUsers.value = [];
      usersError.value = null;
    }
  }

  function loadCurrentSelectedGroup() {
    const savedGroupId = $getItem("current_selected_group");
    if (savedGroupId && groups.value) {
      const group = groups.value.find((g) => g.id === parseInt(savedGroupId));
      if (group) {
        currentSelectedGroup.value = group;
      } else {
        currentSelectedGroup.value = groups.value[0];
      }
    }
  }

  async function getGroups() {
    try {
      const auth = useAuthStore();
      // const data: FamilyGroup[] = await auth.api("GET", `/familygrp/index/`);
      const data: FamilyGroup[] = await auth.api("GET", `/familygrp/my-groups/`);
      groups.value = data;
      return groups.value;
    } catch (error) {
      console.log("getGroups Error", error);
      return null;
    }
  }

  function setGroups(familyGroups: FamilyGroup[], clear: boolean = false) {
    groups.value = familyGroups;

    // If groups array is empty (like during logout), clear everything including localStorage
    if (familyGroups.length === 0 && clear) {
      groups.value = [];
      sortedGroups.value = {};
      currentSelectedGroup.value = null;
      // Clear localStorage items related to groups
      $setItem("fgrp_order", "");
      $setItem("current_selected_group", "");
      return;
    }

    let localOrder = $getItem("fgrp_order");
    const availableGroups = Array.isArray(groups.value) ? groups.value : [];

    if (!localOrder || typeof localOrder !== "string" || localOrder.trim().length === 0) {
      const defaultOrder = availableGroups.map((obj) => obj.id).join(",");
      if (defaultOrder.length > 0) {
        $setItem("fgrp_order", defaultOrder);
        localOrder = $getItem("fgrp_order");
      } else {
        localOrder = "";
      }
    }
    const orderString: string = Array.isArray(localOrder)
      ? localOrder.join(",") // handle deserialized array
      : String(localOrder ?? ""); // handle null, number, object, etc.

    const getGrp = orderString
      .split(",")
      .map((num) => parseInt(num))
      .filter((num) => !Number.isNaN(num));
    let sorted = availableGroups.slice();
    if (getGrp.length > 0) {
      sorted = sortGroupsByReference(availableGroups, getGrp);
    }

    groups.value = sorted;
    sortedGroups.value = {};

    sorted.forEach((grp) => {
      sortedGroups.value[grp.id] = grp;
    });

    // Load the previously selected group after setting groups
    loadCurrentSelectedGroup();
  }

  function setUserGroups(userGroups: FamilyGroup[]) {
    // Set user's available groups and handle auto-selection

    const auth = useAuthStore();

    // Determine available groups based on the actor's view_all permission.
    // view_all is granted to owner, hr_manager, and auditor roles.
    let availableGroups: FamilyGroup[];

    if (auth.permissions?.familyGroups?.includes("view_all")) {
      // view_all actors see all groups
      availableGroups = groups.value || [];
    } else {
      // Other users are limited to their assigned groups
      const userGroupIds = userGroups.map((group) => group.id);
      availableGroups = groups.value?.filter((group) => userGroupIds.includes(group.id)) || [];
    }

    // Auto-selection logic
    if (availableGroups.length === 1) {
      // If only one group, select it automatically
      setCurrentSelectedGroup(availableGroups[0]);
    } else if (availableGroups.length > 1) {
      // If multiple groups but no current selection, select the first one
      const savedGroupId = $getItem("current_selected_group");
      const savedGroup = availableGroups.find((g) => g.id === parseInt(savedGroupId || "0"));

      if (!savedGroup) {
        // No saved selection or saved group not available, select first one
        setCurrentSelectedGroup(availableGroups[0]);
      } else {
        // Use the saved selection
        setCurrentSelectedGroup(savedGroup);
      }
    } else {
      // No available groups, clear users
      currentGroupUsers.value = [];
      usersError.value = null;
    }
  }

  async function updateGroupsOrder(newGroups: number[]) {
    $setItem("fgrp_order", newGroups.toString());
  }

  async function grantUserAccess(targetUserId: number, groupId: number) {
    try {
      const auth = useAuthStore();
      await auth.api("POST", "/familygrp/grant-access/", {
        targetUserId,
        groupId,
      });
      return true;
    } catch (error) {
      console.error("Error granting access:", error);
      return false;
    }
  }

  async function revokeUserAccess(targetUserId: number, groupId: number) {
    try {
      const auth = useAuthStore();
      await auth.api("POST", "/familygrp/revoke-access/", {
        targetUserId,
        groupId,
      });
      return true;
    } catch (error) {
      console.error("Error revoking access:", error);
      return false;
    }
  }

  async function deleteGroup(groupId: number) {
    try {
      const auth = useAuthStore();
      await auth.api("DELETE", "/familygrp/delete/", {
        id: groupId,
      });
      // Refresh groups after deletion
      await getGroups();
      return true;
    } catch (error) {
      console.error("Error deleting group:", error);
      throw error;
    }
  }

  return {
    setGroups,
    groups,
    sortedGroups,
    updateGroupsOrder,
    filterByIds,
    getGroups,
    getGroupInfo,
    getGroupIcon,
    currentSelectedGroup,
    setCurrentSelectedGroup,
    loadCurrentSelectedGroup,
    setUserGroups,
    grantUserAccess,
    revokeUserAccess,
    deleteGroup,
    userGroups,
    currentGroupUsers,
    usersLoading,
    usersError,
    fetchGroupUsers,
    weeklyDigest,
    digestLoading,
    fetchWeeklyDigest,
    weeklyDigests,
    digestsLoading,
    fetchWeeklyDigests,
    digestPage,
    digestTotalPages,
  };
});

acceptHMRUpdate(useGroupStore, (module) =>
  module.acceptHMRUpdate(module, storeToRefs(useGroupStore())),
);
